<template>
    <LandingPageLayout
        v-bind="landingPageSharedData.getLandingPageLayoutProps()"
    >
        <article class="max-w-4xl mx-auto py-4 px-6 lg:px-0 flex-1">
            <div class="space-y-8 mb-20">
                <header>
                    <h1 class="text-3xl font-semibold">Privacy Policy</h1>
                    <p>Last updated: February 18th, 2025</p>
                </header>

                <section>
                    <div class="space-y-4">
                        <p>
                            This Privacy Policy describes how TinyCal (the
                            "Site", "we", "us", or "our") collects, uses, and
                            discloses your personal information when you visit,
                            use our services, or make a purchase from
                            https://tinycal.net (the &quot;Site&quot;) or
                            otherwise communicate with us (collectively, the
                            &quot;Services&quot;). For purposes of this Privacy
                            Policy, &quot;you&quot; and &quot;your&quot; means
                            you as the user of the Services, whether you are a
                            customer, website visitor, or another individual
                            whose information we have collected pursuant to this
                            Privacy Policy.
                        </p>
                        <p>
                            Please read this Privacy Policy carefully. By using
                            and accessing any of the Services, you agree to the
                            collection, use, and disclosure of your information
                            as described in this Privacy Policy. If you do not
                            agree to this Privacy Policy, please do not use or
                            access any of the Services.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        1. Changes to This Privacy Policy
                    </h2>
                    <div class="space-y-4">
                        <p>
                            We may update this Privacy Policy from time to time,
                            including to reflect changes to our practices or for
                            other operational, legal, or regulatory reasons. We
                            will post the revised Privacy Policy on the Site,
                            update the "Last updated" date and take any other
                            steps required by applicable law.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        2. How We Collect and Use Your Personal Information
                    </h2>
                    <div class="space-y-4">
                        <p>
                            To provide the Services, we collect and have
                            collected over the past 12 months personal
                            information about you from a variety of sources, as
                            set out below. The information that we collect and
                            use varies depending on how you interact with us.
                        </p>
                        <p>
                            In addition to the specific uses set out below, we
                            may use information we collect about you to
                            communicate with you, provide the Services, comply
                            with any applicable legal obligations, enforce any
                            applicable terms of service, and to protect or
                            defend the Services, our rights, and the rights of
                            our users or others.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        3. What Personal Information We Collect
                    </h2>
                    <div class="space-y-4">
                        <p>
                            The types of personal information we obtain about
                            you depends on how you interact with our Site and
                            use our Services. When we use the term "personal
                            information", we are referring to information that
                            identifies, relates to, describes or can be
                            associated with you. The following sections describe
                            the categories and specific types of personal
                            information we collect.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        4. Information We Collect Directly from You
                    </h2>
                    <div class="space-y-4">
                        <p>
                            Information that you directly submit to us through
                            our Services may include:
                        </p>
                        <ul class="list-disc ml-8">
                            <li>
                                Basic contact details including your name,
                                address, phone number, email.
                            </li>
                            <li>
                                Order information including your name, billing
                                address, shipping address, payment confirmation,
                                email address, phone number.
                            </li>
                            <li>
                                Account information including your username,
                                password, security questions.
                            </li>
                            <li>
                                Shopping information including the items you
                                view, put in your cart or add to your wishlist.
                            </li>
                            <li>
                                Customer support information including the
                                information you choose to include in
                                communications with us, for example, when
                                sending a message through the Services.
                            </li>
                        </ul>
                        <p>
                            Some features of the Services may require you to
                            directly provide us with certain information about
                            yourself. You may elect not to provide this
                            information, but doing so may prevent you from using
                            or accessing these features.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        5. Information We Collect through Cookies
                    </h2>
                    <div class="space-y-4">
                        <p>
                            We also automatically collect certain information
                            about your interaction with the Services ("Usage
                            Data"). To do this, we may use cookies, pixels and
                            similar technologies ("Cookies"). Usage Data may
                            include information about how you access and use our
                            Site and your account, including device information,
                            browser information, information about your network
                            connection, your IP address and other information
                            regarding your interaction with the Services.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        6. Information We Obtain from Third Parties
                    </h2>
                    <div class="space-y-4">
                        <p>
                            Finally, we may obtain information about you from
                            third parties, including from vendors and service
                            providers who may collect information on our behalf,
                            such as:
                        </p>
                        <ul class="list-disc ml-8">
                            <li>
                                Companies who support our Site and Services.
                            </li>
                            <li>
                                Our payment processors, who collect payment
                                information (e.g., bank account, credit or debit
                                card information, billing address) to process
                                your payment in order to fulfill your orders and
                                provide you with products or services you have
                                requested, in order to perform our contract with
                                you.
                            </li>
                            <li>
                                When you visit our Site, open or click on emails
                                we send you, or interact with our Services or
                                advertisements, we, or third parties we work
                                with, may automatically collect certain
                                information using online tracking technologies
                                such as pixels, web beacons, software developer
                                kits, third-party libraries, and cookies.
                            </li>
                        </ul>
                        <p>
                            Any information we obtain from third parties will be
                            treated in accordance with this Privacy Policy. We
                            are not responsible or liable for the accuracy of
                            the information provided to us by third parties and
                            are not responsible for any third party's policies
                            or practices. For more information, see the section
                            below, Third Party Websites and Links.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        7. How We Use Your Personal Information
                    </h2>
                    <div class="space-y-4">
                        <ul class="list-disc ml-8">
                            <li>
                                Providing Products and Services. We use your
                                personal information to provide you with the
                                Services in order to perform our contract with
                                you, including to process your payments, fulfill
                                your orders, to send notifications to you
                                related to you account, purchases, returns,
                                exchanges or other transactions, to create,
                                maintain and otherwise manage your account, to
                                arrange for shipping, facilitate any returns and
                                exchanges and to enable you to post reviews.
                            </li>
                            <li>
                                Marketing and Advertising. We use your personal
                                information for marketing and promotional
                                purposes, such as to send marketing, advertising
                                and promotional communications by email, text
                                message or postal mail, and to show you
                                advertisements for products or services. This
                                may include using your personal information to
                                better tailor the Services and advertising on
                                our Site and other websites.
                            </li>
                            <li>
                                Security and Fraud Prevention. We use your
                                personal information to detect, investigate or
                                take action regarding possible fraudulent,
                                illegal or malicious activity. If you choose to
                                use the Services and register an account, you
                                are responsible for keeping your account
                                credentials safe. We highly recommend that you
                                do not share your username, password, or other
                                access details with anyone else. If you believe
                                your account has been compromised, please
                                contact us immediately.
                            </li>
                            <li>
                                Communicating with you. We use your personal
                                information to provide you with customer support
                                and improve our Services. This is in our
                                legitimate interests in order to be responsive
                                to you, to provide effective services to you,
                                and to maintain our business relationship with
                                you.
                            </li>
                        </ul>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">8. Cookies</h2>
                    <div class="space-y-4">
                        <p>
                            Like many websites, we use Cookies on our Site. For
                            specific information about the Cookies that we use
                            related to powering our store with Shopify, see
                            https://www.shopify.com/legal/cookies. We use
                            Cookies to power and improve our Site and our
                            Services (including to remember your actions and
                            preferences), to run analytics and better understand
                            user interaction with the Services (in our
                            legitimate interests to administer, improve and
                            optimize the Services). We may also permit third
                            parties and services providers to use Cookies on our
                            Site to better tailor the services, products and
                            advertising on our Site and other websites.
                        </p>
                        <p>
                            Most browsers automatically accept Cookies by
                            default, but you can choose to set your browser to
                            remove or reject Cookies through your browser
                            controls. Please keep in mind that removing or
                            blocking Cookies can negatively impact your user
                            experience and may cause some of the Services,
                            including certain features and general
                            functionality, to work incorrectly or no longer be
                            available. Additionally, blocking Cookies may not
                            completely prevent how we share information with
                            third parties such as our advertising partners.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        9. How We Disclose Personal Information
                    </h2>
                    <div class="space-y-4">
                        <p>
                            In certain circumstances, we may disclose your
                            personal information to third parties for legitimate
                            purposes subject to this Privacy Policy. Such
                            circumstances may include:
                        </p>
                        <ul class="list-disc ml-8">
                            <li>
                                With vendors or other third parties who perform
                                services on our behalf (e.g., IT management,
                                payment processing, data analytics, customer
                                support, cloud storage, fulfillment and
                                shipping).
                            </li>
                            <li>
                                With business and marketing partners, including
                                Shopify, to provide services and advertise to
                                you. [[NOTE TO MERCHANT: INSERT THE FOLLOWING
                                SENTENCE IF USING SHOPIFY'S AD SERVICES, SUCH AS
                                SHOPIFY AUDIENCES]] [For example, we use Shopify
                                to support personalized advertising with
                                third-party services]. Our business and
                                marketing partners will use your information in
                                accordance with their own privacy notices.
                            </li>
                            <li>
                                When you direct, request us or otherwise consent
                                to our disclosure of certain information to
                                third parties, such as to ship you products or
                                through your use of social media widgets or
                                login integrations, with your consent.
                            </li>
                            <li>
                                With our affiliates or otherwise within our
                                corporate group, in our legitimate interests to
                                run a successful business.
                            </li>
                            <li>
                                In connection with a business transaction such
                                as a merger or bankruptcy, to comply with any
                                applicable legal obligations (including to
                                respond to subpoenas, search warrants and
                                similar requests), to enforce any applicable
                                terms of service, and to protect or defend the
                                Services, our rights, and the rights of our
                                users or others.
                            </li>
                        </ul>
                        <p>
                            We have, in the past 12 months disclosed the
                            following categories of personal information and
                            sensitive personal information (denoted by *) about
                            users for the purposes set out above in "How we
                            Collect and Use your Personal Information" and "How
                            we Disclose Personal Information":
                        </p>
                        <p>Category:</p>
                        <ul class="list-disc ml-8">
                            <li>
                                Identifiers such as basic contact details and
                                certain order and account information
                            </li>
                            <li>
                                Commercial information such as order
                                information, shopping information and customer
                                support information
                            </li>
                            <li>
                                Internet or other similar network activity, such
                                as Usage Data
                            </li>
                        </ul>
                        <p>Categories of Recipients:</p>
                        <ul class="list-disc ml-8">
                            <li>
                                Vendors and third parties who perform services
                                on our behalf (such as Internet service
                                providers, payment processors, fulfillment
                                partners, customer support partners and data
                                analytics providers)
                            </li>
                            <li>Business and marketing partners</li>
                            <li>Affiliates</li>
                        </ul>
                        <p>
                            We do not use or disclose sensitive personal
                            information for the purposes of inferring
                            characteristics about you.
                        </p>
                        <p>
                            We have "sold" and "shared" (as those terms are
                            defined in applicable law) personal information over
                            the preceding 12 months for the purpose of engaging
                            in advertising and marketing activities, as follows.
                        </p>
                        <p>Category of Personal Information:</p>
                        <ul class="list-disc ml-8">
                            <li>
                                Identifiers such as basic contact details and
                                certain order and account information
                            </li>
                            <li>
                                Commercial information such as records of
                                products or services purchased and shopping
                                information
                            </li>
                            <li>
                                Internet or other similar network activity, such
                                as Usage Data
                            </li>
                        </ul>
                        <p>Categories of Recipients:</p>
                        <ul class="list-disc ml-8">
                            <li>Business and marketing partners</li>
                        </ul>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        10. User Generated Content
                    </h2>
                    <div class="space-y-4">
                        <p>
                            The Services may enable you to post product reviews
                            and other user-generated content. If you choose to
                            submit user generated content to any public area of
                            the Services, this content will be public and
                            accessible by anyone.
                        </p>
                        <p>
                            We do not control who will have access to the
                            information that you choose to make available to
                            others, and cannot ensure that parties who have
                            access to such information will respect your privacy
                            or keep it secure. We are not responsible for the
                            privacy or security of any information that you make
                            publicly available, or for the accuracy, use or
                            misuse of any information that you disclose or
                            receive from third parties.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        11. Third Party Websites and Links
                    </h2>
                    <div class="space-y-4">
                        <p>
                            Our Site may provide links to websites or other
                            online platforms operated by third parties. If you
                            follow links to sites not affiliated or controlled
                            by us, you should review their privacy and security
                            policies and other terms and conditions. We do not
                            guarantee and are not responsible for the privacy or
                            security of such sites, including the accuracy,
                            completeness, or reliability of information found on
                            these sites. Information you provide on public or
                            semi-public venues, including information you share
                            on third-party social networking platforms may also
                            be viewable by other users of the Services and/or
                            users of those third-party platforms without
                            limitation as to its use by us or by a third party.
                            Our inclusion of such links does not, by itself,
                            imply any endorsement of the content on such
                            platforms or of their owners or operators, except as
                            disclosed on the Services.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">12. Children's Data</h2>
                    <div class="space-y-4">
                        <p>
                            The Services are not intended to be used by
                            children, and we do not knowingly collect any
                            personal information about children. If you are the
                            parent or guardian of a child who has provided us
                            with their personal information, you may contact us
                            using the contact details set out below to request
                            that it be deleted.
                        </p>
                        <p>
                            As of the Effective Date of this Privacy Policy, we
                            do not have actual knowledge that we "share" or
                            "sell" (as those terms are defined in applicable
                            law) personal information of individuals under 16
                            years of age.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        13. Security and Retention of Your Information
                    </h2>
                    <div class="space-y-4">
                        <p>
                            Please be aware that no security measures are
                            perfect or impenetrable, and we cannot guarantee
                            "perfect security." In addition, any information you
                            send to us may not be secure while in transit. We
                            recommend that you do not use unsecure channels to
                            communicate sensitive or confidential information to
                            us.
                        </p>
                        <p>
                            How long we retain your personal information depends
                            on different factors, such as whether we need the
                            information to maintain your account, to provide the
                            Services, comply with legal obligations, resolve
                            disputes or enforce other applicable contracts and
                            policies.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        14. Your Rights and Choices
                    </h2>
                    <div class="space-y-4">
                        <p>
                            Depending on where you live, you may have some or
                            all of the rights listed below in relation to your
                            personal information. However, these rights are not
                            absolute, may apply only in certain circumstances
                            and, in certain cases, we may decline your request
                            as permitted by law.
                        </p>
                        <ul class="list-disc ml-8">
                            <li>
                                Right to Access / Know. You may have a right to
                                request access to personal information that we
                                hold about you, including details relating to
                                the ways in which we use and share your
                                information.
                            </li>
                            <li>
                                Right to Delete. You may have a right to request
                                that we delete personal information we maintain
                                about you.
                            </li>
                            <li>
                                Right to Correct. You may have a right to
                                request that we correct inaccurate personal
                                information we maintain about you.
                            </li>
                            <li>
                                Right of Portability. You may have a right to
                                receive a copy of the personal information we
                                hold about you and to request that we transfer
                                it to a third party, in certain circumstances
                                and with certain exceptions.
                            </li>
                            <li>
                                Right to Opt out of Sale or Sharing or Targeted
                                Advertising. You may have a right to direct us
                                not to "sell" or "share" your personal
                                information or to opt out of the processing of
                                your personal information for purposes
                                considered to be "targeted advertising", as
                                defined in applicable privacy laws. Please note
                                that if you visit our Site with the Global
                                Privacy Control opt-out preference signal
                                enabled, depending on where you are, we will
                                automatically treat this as a request to opt-out
                                of the "sale" or "sharing" of information for
                                the device and browser that you use to visit the
                                Site.
                            </li>
                            <li>
                                Right to Limit and/or Opt out of Use and
                                Disclosure of Sensitive Personal Information.
                                You may have a right to direct us to limit our
                                use and/or disclosure of sensitive personal
                                information to only what is necessary to perform
                                the Services or provide the goods reasonably
                                expected by an average individual.
                            </li>
                            <li>
                                Restriction of Processing: You may have the
                                right to ask us to stop or restrict our
                                processing of personal information.
                            </li>
                            <li>
                                Withdrawal of Consent: Where we rely on consent
                                to process your personal information, you may
                                have the right to withdraw this consent.
                            </li>
                            <li>
                                Appeal: You may have a right to appeal our
                                decision if we decline to process your request.
                                You can do so by replying directly to our
                                denial.
                            </li>
                            <li>
                                Managing Communication Preferences: We may send
                                you promotional emails, and you may opt out of
                                receiving these at any time by using the
                                unsubscribe option displayed in our emails to
                                you. If you opt out, we may still send you
                                non-promotional emails, such as those about your
                                account or orders that you have made.
                            </li>
                        </ul>
                        <p>
                            You may exercise any of these rights where indicated
                            on our Site or by contacting us using the contact
                            details provided below.
                        </p>
                        <p>
                            We will not discriminate against you for exercising
                            any of these rights. We may need to collect
                            information from you to verify your identity, such
                            as your email address or account information, before
                            providing a substantive response to the request. In
                            accordance with applicable laws, You may designate
                            an authorized agent to make requests on your behalf
                            to exercise your rights. Before accepting such a
                            request from an agent, we will require that the
                            agent provide proof you have authorized them to act
                            on your behalf, and we may need you to verify your
                            identity directly with us. We will respond to your
                            request in a timely manner as required under
                            applicable law.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">15. Complaints</h2>
                    <div class="space-y-4">
                        <p>
                            If you have complaints about how we process your
                            personal information, please contact us using the
                            contact details provided below. If you are not
                            satisfied with our response to your complaint,
                            depending on where you live you may have the right
                            to appeal our decision by contacting us using the
                            contact details set out below, or lodge your
                            complaint with your local data protection authority.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        16. International Users
                    </h2>
                    <div class="space-y-4">
                        <p>
                            Please note that we may transfer, store and process
                            your personal information outside the country you
                            live in, including the United States. Your personal
                            information is also processed by staff and third
                            party service providers and partners in these
                            countries.
                        </p>
                        <p>
                            If we transfer your personal information out of
                            Europe, we will rely on recognized transfer
                            mechanisms like the European Commission's Standard
                            Contractual Clauses, or any equivalent contracts
                            issued by the relevant competent authority of the
                            UK, as relevant, unless the data transfer is to a
                            country that has been determined to provide an
                            adequate level of protection.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        17. Access to Your Google Account Information
                    </h2>
                    <div class="space-y-4">
                        <p>Google Calendar:</p>
                        <p>
                            In case you want to use our Google Calendar
                            integration, which is optional, we require you to
                            connect your Google Calendar account and give us
                            permission to:
                        </p>
                        <ul class="list-disc ml-8">
                            <li>
                                List your calendars, so that we can retrieve the
                                ID of the primary one, we need that information
                                to be able to manage events from the correct
                                calendar.
                            </li>
                            <li>
                                Create, update and delete events in your Google
                                calendar, so that meetings scheduled via TinyCal
                                are properly synced to your primary Google
                                calendar. We do not modify calendar events that
                                were not created by TinyCal.
                            </li>
                        </ul>
                        <p>
                            In our database we store the unique ID of your
                            primary calendar and also the unique ID of each
                            calendar event created by TinyCal, we need these
                            pieces of information to be able to:
                        </p>
                        <ul class="list-disc ml-8">
                            <li>
                                Update the external event in you Google Calendar
                                when the corresponding meeting is rescheduled or
                                updated in any way via TinyCal.
                            </li>
                            <li>
                                Delete it from your Google Calendar if the
                                meeting is canceled via TinyCal.
                            </li>
                        </ul>
                        <p>
                            We do not share any data from your Google Calendar
                            account.
                        </p>
                        <p>Google Meet:</p>
                        <p>
                            In case you want to use our Google Meet integration,
                            which is optional, we require you to connect your
                            Google Meet account and give us permission to create
                            new meetings in your connected account, so that
                            every time an meeting is scheduled via TinyCal we
                            automatically create a new meeting link for it.
                        </p>
                        <p>
                            In our database, for each Google Meet meeting
                            created by TinyCal, we store the meeting's name, URL
                            and code, so that we can present that information to
                            you in our interface and also share that information
                            with the meeting's attendees, to make it easy for
                            all parties to join the meeting.
                        </p>
                        <p>
                            The only data we share from your Google Meet account
                            with people outside your TinyCal's account is the
                            URL of the meetings that were exclusively created by
                            TinyCal, and we only share that information with the
                            direct attendees of the meetings, no other piece of
                            information from your Google Meet account is shared.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        18. Access to Your Zoom Account Information
                    </h2>
                    <div class="space-y-4">
                        <p>
                            In case you want to use our Zoom integration, which
                            is optional, we require you to connect your Zoom
                            account and give us permission to create, update and
                            delete meetings in your Zoom account, so that
                            everytime an meeting is scheduled via TinyCal we are
                            able to automatically create meeting links that will
                            be used by the team members and attendees, also
                            whenever an meeting is rescheduled we update the
                            associated Zoom meeting, and finally, whenever an
                            meeting is canceled we delete the corresponding
                            meeting from your Zoom account.
                        </p>
                        <p>
                            In our database, for each Zoom meeting created by
                            TinyCal, we store the meeting's URL and unique ID,
                            so that we can present that information to you in
                            our interface and also share that information with
                            the meeting's attendees, to make it easy for all
                            parties to join the meeting. The unique ID is used
                            by TinyCal to perform the actions mentioned in the
                            previous paragraph.
                        </p>
                        <p>
                            The only data we share from your Zoom account with
                            people outside your TinyCal's account is the URL to
                            join the meetings that were exclusively created by
                            TinyCal, and we only share that information with the
                            direct attendees of the meetings, no other piece of
                            information from your Zoom account is shared.
                        </p>
                        <p>
                            We do not collect any other piece of information
                            that is used for tracking purposes of any nature.
                            Also we don't sell your information or share it with
                            any other companies or applications.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        19. Personal Information Used For Customer Support
                        Purposes
                    </h2>
                    <div class="space-y-4">
                        <p>
                            To provide our users with live customer support
                            chat, we collect and share the following information
                            about our users with our third-party provider.
                        </p>
                        <ul class="list-disc ml-8">
                            <li>Full name</li>
                            <li>Email</li>
                        </ul>
                        <p>
                            Our current customer support software provider is
                            "Tawk.to". Their website is https://www.tawk.to/,
                            and their privacy policy can be found at
                            https://www.tawk.to/privacy-policy/.
                        </p>
                        <p>
                            The information above is used to identify the user
                            requesting support inside the live support software
                            we currently use, which allows us to better
                            troubleshoot issues and provide more accurate
                            support.
                        </p>
                        <p>
                            We do not use the information listed above for
                            marketing purposes.
                        </p>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-xl font-semibold">
                        20. Contact Information
                    </h2>
                    <div class="space-y-4">
                        <p>If you have any questions please contact us at:</p>
                        <p>
                            Name: Isaac Souza Silva
                            <br />
                            CPF: 052.379.873-38
                            <br />
                            Email: contact@tinycal.net
                            <br />
                            Address: Av. 07, Qd. 19, N. 17, Caxias, MA,
                            65604660, Brazil
                        </p>
                    </div>
                </section>
            </div>
        </article>
    </LandingPageLayout>
</template>

<script setup lang="ts">
    import LandingPageLayout from '@/Marketing/LandingPage/Shared/LandingPageLayout.vue'
    import LandingPageSharedData from '@/Marketing/LandingPage/Entity/LandingPageSharedData'

    interface LandingPageProps {
        landingPageSharedDataRawData: any
    }

    const props = defineProps<LandingPageProps>()
    const landingPageSharedData = LandingPageSharedData.hydrate(
        props.landingPageSharedDataRawData,
    )
</script>
